import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Youtube, Headphones, ArrowLeft } from "lucide-react";
import { episodes, getEpisode, type Episode } from "@/lib/podcast-episodes";

const BASE = "https://rajshamani.life";

function parseDurationISO(d: string): string {
  const m = d.match(/(?:(\d+)h)?\s*(?:(\d+)m)?/);
  const h = m?.[1] ? parseInt(m[1], 10) : 0;
  const min = m?.[2] ? parseInt(m[2], 10) : 0;
  return h || min ? `PT${h ? `${h}H` : ""}${min ? `${min}M` : ""}` : "PT0M";
}

export const Route = createFileRoute("/podcast/$slug")({
  loader: ({ params }) => {
    const ep = getEpisode(params.slug);
    if (!ep) throw notFound();
    return { ep };
  },
  head: ({ params, loaderData }) => {
    const ep = loaderData?.ep ?? getEpisode(params.slug);
    if (!ep) {
      return {
        meta: [{ title: "Episode not found | Figuring Out — Raj Shamani" }],
      };
    }
    const url = `${BASE}/podcast/${ep.slug}`;
    const suffix = " | Figuring Out";
    const maxBase = 60 - suffix.length;
    const base = ep.g.length + 3 + ep.t.length > maxBase
      ? `${ep.g}: ${ep.t}`.slice(0, maxBase - 1).trimEnd() + "…"
      : `${ep.g}: ${ep.t}`;
    const title = `${base}${suffix}`;
    const description = `${ep.g} on Figuring Out with Raj Shamani: ${ep.t}. Full episode summary, key takeaways, and complete transcript.`;
    return {
      meta: [
        { title, key: "title" },
        { name: "description", content: description, key: "description" },
        { property: "og:title", content: title, key: "og:title" },
        { property: "og:description", content: description, key: "og:description" },
        { property: "og:url", content: url, key: "og:url" },
        { property: "og:type", content: "article", key: "og:type" },
        { property: "og:site_name", content: "Raj Shamani", key: "og:site_name" },
        { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
        { name: "twitter:title", content: title, key: "twitter:title" },
        { name: "twitter:description", content: description, key: "twitter:description" },
        { name: "robots", content: "index, follow", key: "robots" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "PodcastEpisode",
            name: ep.t,
            url,
            datePublished: ep.publishedAt,
            episodeNumber: parseInt(ep.n.replace(/^EP\s*/, ""), 10),
            duration: parseDurationISO(ep.d),
            description,
            inLanguage: "en",
            partOfSeries: {
              "@type": "PodcastSeries",
              name: "Figuring Out",
              url: `${BASE}/podcast`,
            },
            associatedMedia: {
              "@type": "MediaObject",
              contentUrl: `https://www.youtube.com/watch?v=${ep.youtubeId}`,
            },
            author: { "@type": "Person", name: "Raj Shamani" },
            actor: { "@type": "Person", name: ep.g },
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-6 py-32 text-center">
      <h1 className="text-3xl font-bold mb-3">Episode not found</h1>
      <p className="text-muted-foreground mb-6">We couldn't find that episode.</p>
      <Link to="/podcast" className="text-gold underline">Back to all episodes</Link>
    </div>
  ),
  errorComponent: ({ reset }) => (
    <div className="mx-auto max-w-3xl px-6 py-32 text-center">
      <h1 className="text-3xl font-bold mb-3">Something went wrong</h1>
      <button onClick={reset} className="text-gold underline">Try again</button>
    </div>
  ),
  component: EpisodePage,
});

function EpisodePage() {
  const { ep } = Route.useLoaderData() as { ep: Episode };
  return (
    <div>
      <PageBreadcrumb current={`${ep.n} · ${ep.g}`} />
      <article className="mx-auto max-w-4xl px-6 py-12">
        <Link
          to="/podcast"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-gold mb-6"
        >
          <ArrowLeft size={14} /> All episodes
        </Link>

        <div className="text-xs tracking-widest uppercase text-gold mb-3">
          {ep.n} · {ep.d} · Figuring Out
        </div>
        <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-4">
          {ep.t}
        </h1>
        <p className="text-lg text-muted-foreground mb-8">
          With <span className="text-foreground font-medium">{ep.g}</span> · Hosted by Raj Shamani
        </p>

        <div className="aspect-video w-full rounded-xl overflow-hidden border border-border mb-10 bg-card">
          {ep.youtubeId.startsWith("TBD_") ? (
            <a
              href={`https://www.youtube.com/results?search_query=${encodeURIComponent(`Raj Shamani ${ep.g} Figuring Out`)}`}
              target="_blank"
              rel="noopener"
              className="w-full h-full flex items-center justify-center text-center p-6 text-muted-foreground hover:text-foreground"
            >
              Watch this episode with {ep.g} on YouTube →
            </a>
          ) : (
            <iframe
              src={`https://www.youtube.com/embed/${ep.youtubeId}`}
              title={`${ep.t} — ${ep.g}`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
              className="w-full h-full"
            />
          )}
        </div>

        <div className="flex flex-wrap gap-3 mb-12">
          <a
            href={ep.youtubeId.startsWith("TBD_")
              ? `https://www.youtube.com/results?search_query=${encodeURIComponent(`Raj Shamani ${ep.g} Figuring Out`)}`
              : `https://www.youtube.com/watch?v=${ep.youtubeId}`}
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gold/40 text-sm hover:bg-gold/10"
          >
            <Youtube size={16} /> Watch on YouTube
          </a>

          <a
            href="https://open.spotify.com"
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gold/40 text-sm hover:bg-gold/10"
          >
            <Headphones size={16} /> Listen on Spotify
          </a>
        </div>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">Episode summary</h2>
          <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
            {ep.summary}
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">Key takeaways</h2>
          <ul className="space-y-3">
            {ep.takeaways.map((k, i) => (
              <li key={i} className="flex gap-3">
                <span className="text-gold font-bold shrink-0">{i + 1}.</span>
                <span className="text-muted-foreground leading-relaxed">{k}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">Full transcript</h2>
          <p className="text-xs text-muted-foreground mb-6">
            Transcript edited lightly for readability. Timestamps refer to the YouTube video above.
          </p>
          <div className="space-y-5">
            {ep.transcript.map((line, i) => (
              <div key={i} className="grid grid-cols-[80px_1fr] gap-4">
                <div className="text-xs text-muted-foreground pt-1">
                  <div className="font-mono">{line.time}</div>
                  <div className={line.speaker === "Raj" ? "text-gold" : "text-foreground"}>
                    {line.speaker === "Raj" ? "Raj" : ep.g}
                  </div>
                </div>
                <p className="text-muted-foreground leading-relaxed">{line.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="border-t border-border pt-10">
          <h2 className="text-xl font-bold mb-5">More episodes</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {episodes
              .filter((e) => e.slug !== ep.slug)
              .slice(0, 4)
              .map((e) => (
                <Link
                  key={e.slug}
                  to="/podcast/$slug"
                  params={{ slug: e.slug }}
                  className="p-4 rounded-lg border border-border hover:border-gold/40 transition"
                >
                  <div className="text-xs text-gold uppercase tracking-widest mb-1">
                    {e.n} · {e.d}
                  </div>
                  <div className="font-bold leading-tight">{e.t}</div>
                  <div className="text-sm text-muted-foreground mt-1">with {e.g}</div>
                </Link>
              ))}
          </div>
        </section>
      </article>
    </div>
  );
}
